import { useEffect, useMemo, useState } from "react";
import { Gantt, Tooltip, Willow } from "@svar-ui/react-gantt";
import "@svar-ui/react-gantt/all.css";
import {
  RECRUITMENT_REFERENCE_STATUSES,
  normalizeReferenceStatus,
} from "./constants/recruitment";
import { OUTLINED_STATUS_BUTTON_COLORS } from "../../DesignSystem/Buttons/OutlinedStatusButtons";

const SIX_YEARS = 6;
const ONE_DAY = 86400000;
const TASK_ROW_HEIGHT = 42;
const TASK_TYPES = Object.freeze([
  { id: "task", label: "Employment" },
  { id: "employment-gap", label: "Employment gap" },
  { id: "layout-spacer", label: "" },
]);

const formatYear = (date) => String(date.getFullYear());
const formatDate = (date) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);

const createLocalDate = (year, month, day) => {
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));

  return parsed.getFullYear() === Number(year) &&
    parsed.getMonth() === Number(month) - 1 &&
    parsed.getDate() === Number(day)
    ? parsed
    : null;
};

const parseDateString = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Firestore console-style timestamp, for example:
  // "July 27, 2026 at 6:56:28 PM UTC+1".
  const firestoreDisplayDate = trimmed.match(
    /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),\s+(\d{4})\s+at\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s+(AM|PM)\s+UTC([+-]\d{1,2})(?::?(\d{2}))?$/i,
  );
  if (firestoreDisplayDate) {
    const monthNames = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ];
    const month = monthNames.indexOf(firestoreDisplayDate[1].toLowerCase());
    const day = Number(firestoreDisplayDate[2]);
    const year = Number(firestoreDisplayDate[3]);
    let hour = Number(firestoreDisplayDate[4]) % 12;
    if (firestoreDisplayDate[7].toUpperCase() === "PM") hour += 12;
    const minute = Number(firestoreDisplayDate[5]);
    const second = Number(firestoreDisplayDate[6] || 0);
    const offsetSign = firestoreDisplayDate[8].startsWith("-") ? -1 : 1;
    const offsetMinutes =
      offsetSign *
      (Math.abs(Number(firestoreDisplayDate[8])) * 60 +
        Number(firestoreDisplayDate[9] || 0));
    const timestamp =
      Date.UTC(year, month, day, hour, minute, second) - offsetMinutes * 60000;
    const parsed = new Date(timestamp);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  // UK date: DD/MM/YYYY, DD-MM-YYYY or DD.MM.YYYY.
  const ukDate = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (ukDate) return createLocalDate(ukDate[3], ukDate[2], ukDate[1]);

  // ISO/database date without a time: YYYY-MM-DD, YYYY/MM/DD or YYYY.MM.DD.
  const yearFirstDate = trimmed.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);
  if (yearFirstDate) {
    return createLocalDate(
      yearFirstDate[1],
      yearFirstDate[2],
      yearFirstDate[3],
    );
  }

  if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
    return toDate(Number(trimmed));
  }

  const timestamp = Date.parse(trimmed);
  return Number.isNaN(timestamp) ? null : new Date(timestamp);
};

export const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  if (typeof value?.toDate === "function") {
    return toDate(value.toDate());
  }

  if (typeof value === "object") {
    const seconds = value.seconds ?? value._seconds;
    const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;

    if (Number.isFinite(Number(seconds))) {
      return toDate(Number(seconds) * 1000 + Number(nanoseconds) / 1000000);
    }
  }

  if (typeof value === "number") {
    // Unix timestamps may be stored in seconds or milliseconds.
    const milliseconds = Math.abs(value) < 100000000000 ? value * 1000 : value;
    const parsed = new Date(milliseconds);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === "string") {
    return parseDateString(value);
  }

  return null;
};

const startOfYear = (date) => new Date(date.getFullYear(), 0, 1);
const endOfYear = (date) => new Date(date.getFullYear(), 11, 31);
const toLocalCalendarDate = (date) =>
  date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
const getReferenceStatus = (item) => {
  const storedStatus = normalizeReferenceStatus(
    item?.referenceStatus || item?.statusOfReference || "",
  );

  const awaitingAdviserStatus = RECRUITMENT_REFERENCE_STATUSES.find(
    (status) => status.id === "awaiting-adviser",
  );

  return (
    RECRUITMENT_REFERENCE_STATUSES.find(
      (status) =>
        status.id === storedStatus ||
        normalizeReferenceStatus(status.label) === storedStatus,
    ) || awaitingAdviserStatus
  );
};

export const isEmploymentHistory = (item) => {
  const status = String(
    item?.currentEmploymentStatus ||
      item?.employmentStatus ||
      item?.typeOfEmployment ||
      "",
  )
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

  if (status) {
    return ["employed", "selfemployed", "ineducation"].includes(status);
  }

  return (
    item?.referenceType === "regulated" ||
    item?.referenceType === "non-regulated" ||
    item?.referenceType === "educational"
  );
};

const isUnemploymentHistory = (item) =>
  String(
    item?.currentEmploymentStatus ||
      item?.employmentStatus ||
      item?.typeOfEmployment ||
      "",
  )
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "") === "unemployed";

export const isCurrentlyEmployedAtFirm = (value) =>
  value === true || ["true", "yes"].includes(String(value || "").toLowerCase());

const WorkHistoryTooltip = ({ data }) => {
  const task = data?.task;
  if (!task?.start || !task?.end || task.type === "layout-spacer") return null;

  return (
    <div className="work-history-tooltip">
      <div>{task.text}</div>
      <div>
        {formatDate(task.displayStart || task.start)} –{" "}
        {task.isCurrent ? "Present" : formatDate(task.displayEnd || task.end)}
      </div>
    </div>
  );
};

const getReferenceStatusStyle = (statusId) => {
  const status = RECRUITMENT_REFERENCE_STATUSES.find(
    (candidate) => candidate.id === statusId,
  );
  const colors = status
    ? OUTLINED_STATUS_BUTTON_COLORS[status.color]
    : null;

  return colors
    ? {
        "--reference-bar-color": colors.background,
        "--reference-bar-text": colors.text,
        "--reference-bar-border": colors.border,
      }
    : undefined;
};

const WorkHistoryTaskContent = ({ data, onSelect }) => (
  <div
    className={`wx-content ${data.referenceStatusId ? "reference-status-bar" : ""} ${
      data.type === "employment-gap" ? "employment-gap-content" : ""
    } ${data.type === "layout-spacer" ? "layout-spacer-content" : ""} ${
      data.referenceStatusId
        ? `reference-status-${data.referenceStatusId}`
        : ""
    }`}
    style={getReferenceStatusStyle(data.referenceStatusId)}
    onClick={() => data.historyId && onSelect?.(data.historyId)}
  >
    {data.text}
  </div>
);

const WorkHistoryGantt = ({
  workHistory = {},
  onHistorySelect,
  selectedHistoryId,
}) => {
  const [ganttApi, setGanttApi] = useState(null);
  const { tasks, timelineStart, timelineEnd, initialVisibleDate } =
    useMemo(() => {
      const today = new Date();
      const sixYearsAgo = new Date(
        today.getFullYear() - SIX_YEARS,
        today.getMonth(),
        1,
      );
      const sixYearStatusCutoff = new Date(
        today.getFullYear() - SIX_YEARS,
        today.getMonth(),
        today.getDate(),
      );

      const parsedTasks = Object.entries(workHistory)
        .filter(
          ([, item]) =>
            isEmploymentHistory(item) || isUnemploymentHistory(item),
        )
        .map(([key, item]) => {
          if (!item || typeof item !== "object") return null;

          const isUnemployed = isUnemploymentHistory(item);
          const isCurrent =
            !isUnemployed &&
            isCurrentlyEmployedAtFirm(item.currentlyEmployedAtFirm);
          const isEducationHistory =
            item?.referenceType === "educational" ||
            String(
              item?.currentEmploymentStatus ||
                item?.employmentStatus ||
                item?.typeOfEmployment ||
                "",
            )
              .toLowerCase()
              .replace(/[^a-z]/g, "") === "ineducation";
          const hasStoredReferenceStatus = Boolean(
            String(item?.referenceStatus || item?.statusOfReference || "").trim(),
          );

          let start = toLocalCalendarDate(toDate(item.from));
          let end = isCurrent
            ? toLocalCalendarDate(today)
            : toLocalCalendarDate(toDate(item.to)) ||
              toLocalCalendarDate(today);
          if (!start || !end) return null;

          if (start > end) [start, end] = [end, start];

          return {
            id: `work-history-${key}`,
            historyId: isUnemployed ? undefined : key,
            text: isUnemployed
              ? "Employment gap"
              : isEducationHistory
              ? "In Education"
              : item.nameOfFirm || item.currentEmploymentStatus || "Work history",
            start,
            end,
            displayStart: new Date(start.getTime()),
            displayEnd: new Date(end.getTime()),
            isCurrent,
            duration: Math.max(
              1,
              Math.ceil((end.getTime() - start.getTime()) / 86400000),
            ),
            progress: 100,
          type: isUnemployed ? "employment-gap" : "task",
          parent: 0,
          referenceStatusId:
            isUnemployed
              ? undefined
              : !hasStoredReferenceStatus && end < sixYearStatusCutoff
              ? "action-required"
              : getReferenceStatus(item).id,
        };
        })
        .filter(Boolean)
        .sort((first, second) => first.start - second.start);

      const tasksWithGaps = [];
      let coveredUntil = null;

      parsedTasks.forEach((task, index) => {
        const gapStart = coveredUntil
          ? new Date(coveredUntil.getTime() + ONE_DAY)
          : null;

        if (gapStart && task.start > gapStart) {
          const gapEnd = new Date(task.start.getTime() - ONE_DAY);
          tasksWithGaps.push({
            id: `employment-gap-${index}`,
            text: "Employment gap",
            start: gapStart,
            end: gapEnd,
            displayStart: new Date(gapStart.getTime()),
            displayEnd: new Date(gapEnd.getTime()),
            duration: Math.max(
              1,
              Math.ceil((gapEnd.getTime() - gapStart.getTime()) / ONE_DAY),
            ),
            progress: 0,
            type: "employment-gap",
            parent: 0,
          });
        }

        tasksWithGaps.push(task);
        if (!coveredUntil || task.end > coveredUntil) coveredUntil = task.end;
      });

      const hasCurrentEmployment = parsedTasks.some((task) => task.isCurrent);
      const trailingGapStart = coveredUntil
        ? new Date(coveredUntil.getTime() + ONE_DAY)
        : null;

      if (
        !hasCurrentEmployment &&
        trailingGapStart &&
        trailingGapStart <= today
      ) {
        tasksWithGaps.push({
          id: "employment-gap-to-present",
          text: "Employment gap",
          start: trailingGapStart,
          end: today,
          displayStart: new Date(trailingGapStart.getTime()),
          displayEnd: new Date(today.getTime()),
          duration: Math.max(
            1,
            Math.ceil((today.getTime() - trailingGapStart.getTime()) / ONE_DAY),
          ),
          progress: 0,
          type: "employment-gap",
          parent: 0,
        });
      }

      const earliestDate = parsedTasks.reduce(
        (earliest, task) => (task.start < earliest ? task.start : earliest),
        sixYearsAgo,
      );
      const latestDate = parsedTasks.reduce(
        (latest, task) => (task.end > latest ? task.end : latest),
        today,
      );

      const layoutSpacer = {
        id: "work-history-layout-spacer",
        text: "",
        start: today,
        end: new Date(today.getTime() + ONE_DAY),
        duration: 1,
        progress: 0,
        type: "layout-spacer",
        parent: 0,
      };

      return {
        tasks: tasksWithGaps.length ? [...tasksWithGaps, layoutSpacer] : [],
        timelineStart: startOfYear(earliestDate),
        timelineEnd: endOfYear(latestDate),
        initialVisibleDate: sixYearsAgo,
      };
    }, [workHistory]);

  useEffect(() => {
    if (!ganttApi || !selectedHistoryId) return;

    const selectedTask = tasks.find(
      (task) => task.historyId === selectedHistoryId,
    );
    if (!selectedTask) return;

    ganttApi.exec("select-task", {
      id: selectedTask.id,
      toggle: false,
      focus: "chart",
    });
  }, [ganttApi, selectedHistoryId, tasks]);

  if (!tasks.length) {
    return (
      <div className="work-history-empty Content-Small p-l bg-gray-1 text-grey card-corner-radius">
        No dated work history is available for this application.
      </div>
    );
  }

  const initialiseChart = (api) => {
    window.requestAnimationFrame(() => {
      api.exec("scroll-chart", { date: initialVisibleDate });
    });
  };

  const chartSizeClass =
    tasks.length <= 4
      ? "work-history-gantt--small"
      : tasks.length <= 8
        ? "work-history-gantt--medium"
        : tasks.length <= 12
          ? "work-history-gantt--large"
          : "work-history-gantt--xlarge";

  return (
    <div
      className={`work-history-gantt ${chartSizeClass}`}
      aria-label="Applicant work history"
    >
      <Willow fonts={false}>
        <Tooltip api={ganttApi} content={WorkHistoryTooltip}>
          <Gantt
            ref={setGanttApi}
            tasks={tasks}
            taskTypes={TASK_TYPES}
            links={[]}
            columns={false}
            displayMode="chart"
            readonly
            taskTemplate={(props) => (
              <WorkHistoryTaskContent
                {...props}
                onSelect={onHistorySelect}
              />
            )}
            start={timelineStart}
            end={timelineEnd}
            lengthUnit="month"
            cellWidth={168}
            cellHeight={TASK_ROW_HEIGHT}
            scaleHeight={32}
            cellBorders="full"
            scales={[{ unit: "year", step: 1, format: formatYear }]}
            init={initialiseChart}
          />
        </Tooltip>
      </Willow>
    </div>
  );
};

export default WorkHistoryGantt;
